'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import Link from 'next/link';
import { XCircle } from 'lucide-react';

import { copy } from '@/data/copy';
import { Button } from '@/components/ui/button';

export type ErrorBoundaryDefaultProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

type ErrorBoundaryBaseProps = {
  description?: string;
  icon?: React.ReactNode;
  resetActionLabel?: string;
  hideSecondaryAction?: boolean;
  errorHint?: string;
} & ErrorBoundaryDefaultProps;

export function ErrorBoundaryBase({
  description,
  icon,
  resetActionLabel,
  error,
  reset,
  hideSecondaryAction = false,
  errorHint = 'Error:',
}: ErrorBoundaryBaseProps) {
  useEffect(() => {
    Sentry.captureException(error, {
      tags: {
        section: errorHint,
      },
    });
    console.error(errorHint, error);
  }, [error, errorHint]);

  return (
    <div className="flex items-center justify-center h-full dark:bg-boxdark-2 dark:text-bodydark">
      <div className="flex flex-col items-center gap-4 p-8 text-center max-w-md content-center">
        {icon || <XCircle className="w-12 h-12 text-red-500" />}

        <h2 className="text-2xl font-semibold">
          {error.message || copy.common.somethingWentWrong}
        </h2>

        <p className="text-gray-500 text-sm">
          {description || copy.errorBoundary.unexpectedErrorDescription}
        </p>

        <div className="flex gap-3 pt-2">
          <Button onClick={reset}>
            {resetActionLabel || copy.errorBoundary.tryAgain}
          </Button>
          {!hideSecondaryAction && (
            <Button variant="outline" asChild>
              <Link href="/">{copy.errorBoundary.returnToDashboard}</Link>
            </Button>
          )}
        </div>

        {error.digest && (
          <p className="text-xs text-gray-400 mt-2">
            {copy.errorBoundary.errorId} {error.digest}
          </p>
        )}
      </div>
    </div>
  );
}
